import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import { POWorkflowTable } from './POWorkflowTable';
import { DashboardTab } from './DashboardTab';
import styles from './po_workflow_management.module.css';

export const POWorkflowManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const validTabs = useMemo(
    () =>
      new Set([
        'dashboard',
        'action-required',
        'stopped-invoices',
        'all-invoices',
        'archive',
        'all-invoices-full',
      ]),
    []
  );

  const [selectedTab, setSelectedTab] = useState(() => {
    const tab = (searchParams.get('tab') || '').trim();
    return validTabs.has(tab) ? tab : 'action-required';
  });
  const permissionNames = useMemo(() => {
    try {
      const stored = localStorage.getItem('permissions');
      if (!stored) {
        return [];
      }
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map((p) => p.name) : [];
    } catch (error) {
      console.error('Failed to parse permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    if (!permissionNames.includes('Admin Invoice Management')) {
      navigate('/rov', { replace: true });
    }
  }, [permissionNames, navigate]);

  // Keep tab in sync with URL so browser back/forward preserves it.
  useEffect(() => {
    const tab = (searchParams.get('tab') || '').trim();
    if (tab && validTabs.has(tab) && tab !== selectedTab) {
      setSelectedTab(tab);
    }
    if (!tab && selectedTab !== 'action-required') {
      // If URL has no tab param, prefer default.
      setSelectedTab('action-required');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, validTabs]);

  useEffect(() => {
    const current = (searchParams.get('tab') || '').trim();
    if (selectedTab === 'action-required') {
      if (current) {
        setSearchParams({}, { replace: true });
      }
      return;
    }
    if (current !== selectedTab) {
      setSearchParams({ tab: selectedTab }, { replace: true });
    }
  }, [selectedTab, searchParams, setSearchParams]);

  const handleBackClick = () => {
    navigate('/rov');
  };

  return (
    <>
      <DashboardLayout padding={true}>
        <div className={styles['po-workflow-container']}>
          <div className={styles['po-workflow-header']}>
            <div className={styles['header-left']}>
              <div className={styles['back-icon-container']} onClick={handleBackClick}>
                <ArrowBackIosOutlinedIcon className={styles['back-icon']} />
              </div>
              <h1 className={styles['page-title']}>Vendor Invoice Management</h1>
            </div>
          </div>

          {/* Tabs */}
          <div className={styles['tabs-container']}>
            <button
              className={`${styles['tab']} ${selectedTab === 'dashboard' ? styles['tab-active'] : ''}`}
              onClick={() => setSelectedTab('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`${styles['tab']} ${selectedTab === 'action-required' ? styles['tab-active'] : ''}`}
              onClick={() => setSelectedTab('action-required')}
            >
              Active POs
            </button>
            <button
              className={`${styles['tab']} ${selectedTab === 'stopped-invoices' ? styles['tab-active'] : ''}`}
              onClick={() => setSelectedTab('stopped-invoices')}
            >
              Stopped Invoices(POs)
            </button>
            <button
              className={`${styles['tab']} ${selectedTab === 'all-invoices' ? styles['tab-active'] : ''}`}
              onClick={() => setSelectedTab('all-invoices')}
            >
              Closed POs
            </button>
            <button
              className={`${styles['tab']} ${selectedTab === 'archive' ? styles['tab-active'] : ''}`}
              onClick={() => setSelectedTab('archive')}
            >
              Archived POs
            </button>
            <button
              className={`${styles['tab']} ${selectedTab === 'all-invoices-full' ? styles['tab-active'] : ''}`}
              onClick={() => setSelectedTab('all-invoices-full')}
            >
              All Invoice
            </button>
          </div>

          {/* Tab Content */}
          {selectedTab === 'dashboard' && <DashboardTab showPoSummary={false} />}
          {selectedTab !== 'dashboard' && <POWorkflowTable selectedTab={selectedTab} />}
        </div>
      </DashboardLayout>
    </>
  );
};
